import { useEffect, useState } from 'react';
import { Select } from '@wundo/ui';
import {
  contextSpeakerChoiceSupported,
  listAudioDevices,
  type AudioDevices,
} from '@/lib/audioDevices.ts';
import { audioDeviceOptions } from '@/lib/audioDeviceOptions.ts';
import { m } from '@/paraglide/messages.js';

export interface PracticeRealtimeSettingsProps {
  /** Null until the browser has listed what is plugged in. */
  microphoneId: string | null;
  speakerId: string | null;
  onPickMicrophone: (deviceId: string) => void;
  onPickSpeaker: (deviceId: string) => void;
}

const NO_DEVICES: AudioDevices = { microphones: [], speakers: [] };

/**
 * Which microphone and which speaker, on a call that has no room to ask.
 *
 * The list is read straight from the browser: a realtime call holds the
 * microphone itself, so there is no library sitting between this dialog and the
 * device. Switching is the call's own job — this only says what was picked.
 */
export function PracticeRealtimeSettings({
  microphoneId,
  speakerId,
  onPickMicrophone,
  onPickSpeaker,
}: PracticeRealtimeSettingsProps) {
  const [devices, setDevices] = useState<AudioDevices>(NO_DEVICES);

  useEffect(() => {
    let gone = false;

    void listAudioDevices()
      .then((listed) => {
        if (!gone) {
          setDevices(listed);
        }
      })
      .catch(() => {
        /* The pickers stay empty; the call runs on whatever it took. */
      });

    return () => {
      gone = true;
    };
  }, []);

  return (
    <>
      <label className='flex flex-col gap-2 text-base font-semibold text-ink'>
        {m.practice_session_audio_microphone()}
        <Select
          value={microphoneId ?? ''}
          options={audioDeviceOptions(devices.microphones, (index) =>
            m.practice_session_audio_device_microphone({ index }),
          )}
          onChange={(event) => onPickMicrophone(event.target.value)}
        />
      </label>

      {contextSpeakerChoiceSupported() ? (
        <label className='flex flex-col gap-2 text-base font-semibold text-ink'>
          {m.practice_session_audio_speaker()}
          <Select
            value={speakerId ?? ''}
            options={audioDeviceOptions(devices.speakers, (index) =>
              m.practice_session_audio_device_speaker({ index }),
            )}
            onChange={(event) => onPickSpeaker(event.target.value)}
          />
        </label>
      ) : (
        <p className='text-base text-ink-muted'>
          {m.practice_session_audio_speaker_unsupported()}
        </p>
      )}
    </>
  );
}
